import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Video, Calendar, Clock, Play, ExternalLink, User, GraduationCap, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { resolveUploadUrl } from '../../utils/media';

const LiveClassesPage = ({ onMenuClick }) => {
  const [liveSessions, setLiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const getAvatarUrl = (avatarUrl) => resolveUploadUrl(avatarUrl);

  useEffect(() => {
    fetchLiveSessions();
  }, []);

  const fetchLiveSessions = async () => {
    try {
      // Students use their specific endpoint
      const response = await api.get('/live-sessions/student/sessions');
      setLiveSessions(response.data);
    } catch (error) {
      console.error('Error fetching live sessions:', error);
      toast.error('Erreur lors du chargement des sessions');
    } finally {
      setLoading(false);
    }
  };

  const isUpcoming = (session) => {
    if (!session.scheduled_at) return false;
    const start = new Date(session.scheduled_at);
    const end = new Date(start.getTime() + (session.duration_minutes || 60) * 60000);
    return end > new Date();
  };

  const isPast = (session) => {
    if (!session.scheduled_at) return false;
    const start = new Date(session.scheduled_at);
    const end = new Date(start.getTime() + (session.duration_minutes || 60) * 60000);
    return end <= new Date();
  };

  const upcomingSessions = liveSessions.filter(isUpcoming);
  const pastSessions = liveSessions.filter(isPast);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  const SessionCard = ({ session, isPast = false }) => (
    <Card className="hover:shadow-lg transition-all" data-testid={`live-session-card-${session.id}`}>
      <CardContent className="p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
            <Video className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{session.title}</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">{session.description}</p>
            
            <div className="space-y-2">
              {/* Trainer with avatar */}
              {session.trainer_name && (
                <div className="flex items-center gap-3 p-2 bg-slate-50 dark:bg-slate-700 rounded-lg w-fit">
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center">
                    {session.trainer_avatar_url ? (
                      <img 
                        src={getAvatarUrl(session.trainer_avatar_url)} 
                        alt={session.trainer_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <GraduationCap className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      {session.trainer_name}
                    </p>
                    {session.trainer_specialization && (
                      <p className="text-xs text-emerald-600">
                        {session.trainer_specialization}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {session.scheduled_at && (
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <Calendar className="w-4 h-4 text-violet-600" />
                  <span>{new Date(session.scheduled_at).toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</span>
                </div>
              )}
              {session.duration_minutes && (
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <Clock className="w-4 h-4 text-violet-600" />
                  <span>Durée: {session.duration_minutes} minutes</span>
                </div>
              )}
              
              {/* Meeting Link Display */}
              {(session.live_url || session.replay_url) && (
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 px-3 py-2 rounded-lg">
                  <ExternalLink className="w-3 h-3" />
                  <span className="truncate">{isPast ? 'Lien replay disponible' : 'Lien Zoom/Meet disponible'}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          {!isPast && session.live_url && (
            <Button className="flex-1" data-testid={`join-live-btn-${session.id}`} onClick={() => window.open(session.live_url, '_blank')}>
              <Play className="w-4 h-4 mr-2" />
              Rejoindre la session
            </Button>
          )}
          {isPast && session.replay_url && (
            <Button className="flex-1" variant="outline" data-testid={`watch-replay-btn-${session.id}`} onClick={() => window.open(session.replay_url, '_blank')}>
              <Play className="w-4 h-4 mr-2" />
              Voir le replay
            </Button>
          )}
          {!session.replay_url && isPast && (
            <Badge variant="secondary" className="flex-1 justify-center py-2">
              Replay bientôt disponible
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Cours en Direct" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">Cours en Direct & Replays</h2>
          <p className="text-slate-600 dark:text-slate-400">
            {upcomingSessions.length} session(s) à venir • {pastSessions.length} replay(s) disponible(s)
          </p>
        </div>

        <Tabs defaultValue="upcoming" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="upcoming" data-testid="upcoming-tab">
              <Calendar className="w-4 h-4 mr-2" />
              À venir ({upcomingSessions.length})
            </TabsTrigger>
            <TabsTrigger value="replays" data-testid="replays-tab">
              <Play className="w-4 h-4 mr-2" />
              Replays ({pastSessions.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="space-y-6">
            {upcomingSessions.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {upcomingSessions.map((session) => (
                  <SessionCard key={session.id} session={session} />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 dark:text-slate-400">Aucune session planifiée</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="replays" className="space-y-6">
            {pastSessions.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {pastSessions.map((session) => (
                  <SessionCard key={session.id} session={session} isPast={true} />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Video className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 dark:text-slate-400">Aucun replay disponible</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default LiveClassesPage;